/**
 * A vendor-store connect (Jobber's App Marketplace) the owner opened at `/connect/claim` before they had a Symphony
 * account. The page saves the token and sends them to `/welcome`; once the account exists, `usePendingConnectClaimResume`
 * brings them back to finish it, as `usePendingGroupResume` does for a group invite. Expires with the parked connect
 * on the server (30 minutes), so a stale token never hijacks a later visit.
 */
const KEY = 'symphony-pending-connect-claim';
const MAX_AGE_MS = 30 * 60 * 1000;

export const CONNECT_CLAIM_PATH = '/connect/claim';

export function savePendingConnectClaim(token: string, now = Date.now()): void {
  try {
    window.localStorage.setItem(KEY, JSON.stringify({ token, at: now }));
  } catch {
    // private mode / quota — the owner connects again from the app store
  }
}

export function readPendingConnectClaim(now = Date.now()): string | null {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { token?: unknown; at?: unknown };
    if (typeof parsed.token !== 'string' || typeof parsed.at !== 'number' || now - parsed.at > MAX_AGE_MS) {
      window.localStorage.removeItem(KEY);
      return null;
    }
    return parsed.token;
  } catch {
    return null;
  }
}

export function clearPendingConnectClaim(): void {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    // nothing to clear
  }
}

/** Where to send a reader with a parked claim, or null: not before the account exists, and not from `/welcome`
 * (still creating it) or the claim page itself (it owns the claim). */
export function pendingConnectClaimHref(pathname: string, accountReady: boolean, now = Date.now()): string | null {
  if (!accountReady || pathname.startsWith('/welcome') || pathname.startsWith(CONNECT_CLAIM_PATH)) return null;
  const token = readPendingConnectClaim(now);
  return token ? `${CONNECT_CLAIM_PATH}?token=${encodeURIComponent(token)}` : null;
}
